import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, TrendingDown } from 'lucide-react';

const QuickBetCard = ({ symbol, data, onBet }) => {
  const [betAmount, setBetAmount] = useState(25);
  const [showDetails, setShowDetails] = useState(false);

  const getChangeColor = (change) => {
    return change >= 0 ? 'text-green-400' : 'text-red-400';
  };

  const getChangeIcon = (change) => {
    return change >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />;
  };

  const calculatePotentialWin = (direction) => {
    // Simple calculation for demo - in reality this would come from AI analysis
    const baseMultiplier = Math.abs(data.changePercent) > 2 ? 3.5 : 2.8;
    const volatilityBonus = data.volatility > 0.3 ? 1.2 : 1.0;
    return Math.round(betAmount * baseMultiplier * volatilityBonus);
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="casino-card p-4 relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-500"></div>
      </div>

      <div className="relative z-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <span className="text-2xl font-bold text-white">{symbol}</span>
            <span className={`text-sm flex items-center space-x-1 ${getChangeColor(data.changePercent)}`}>
              {getChangeIcon(data.changePercent)}
              <span>{data.changePercent >= 0 ? '+' : ''}{data.changePercent.toFixed(2)}%</span>
            </span>
          </div>
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="text-gray-400 hover:text-white text-sm"
          >
            {showDetails ? 'Less' : 'More'}
          </button>
        </div>

        {/* Price Info */}
        <div className="mb-4">
          <div className="text-xl font-bold text-white mb-1">
            ${data.price.toFixed(2)}
          </div>
          {showDetails && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="text-sm text-gray-400 space-y-1"
            >
              <div>Vol: {(data.volatility * 100).toFixed(1)}%</div>
              <div>Volume: {(data.volume / 1000000).toFixed(1)}M</div>
              <div>Bid/Ask: ${data.bid.toFixed(2)} / ${data.ask.toFixed(2)}</div>
            </motion.div>
          )}
        </div>

        {/* Bet Amount Selector */}
        <div className="mb-4">
          <label className="block text-sm text-gray-400 mb-2">Bet Amount</label>
          <div className="flex space-x-2">
            {[10, 25, 50, 100].map((amount) => (
              <button
                key={amount}
                onClick={() => setBetAmount(amount)}
                className={`px-3 py-1 rounded text-sm transition-all ${
                  betAmount === amount
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                }`}
              >
                ${amount}
              </button>
            ))}
          </div>
        </div>

        {/* Bet Buttons */}
        <div className="grid grid-cols-2 gap-2">
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onBet(symbol, 1, betAmount)}
            className="bg-gradient-to-r from-green-500 to-green-600 text-white py-3 px-4 rounded-lg font-bold text-sm hover:from-green-600 hover:to-green-700 transition-all duration-200 shadow-lg"
          >
            <div className="flex items-center justify-center space-x-1">
              <TrendingUp className="w-4 h-4" />
              <span>UP</span>
            </div>
            <div className="text-xs opacity-90">
              Win ${calculatePotentialWin(1)}
            </div>
          </motion.button>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => onBet(symbol, -1, betAmount)}
            className="bg-gradient-to-r from-red-500 to-red-600 text-white py-3 px-4 rounded-lg font-bold text-sm hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-lg"
          >
            <div className="flex items-center justify-center space-x-1">
              <TrendingDown className="w-4 h-4" />
              <span>DOWN</span>
            </div>
            <div className="text-xs opacity-90">
              Win ${calculatePotentialWin(-1)}
            </div>
          </motion.button>
        </div>

        {/* AI Confidence Indicator */}
        <div className="mt-3 flex items-center justify-between text-xs text-gray-400">
          <span>🤖 AI Confidence</span>
          <div className="flex items-center space-x-1">
            <div className="w-16 h-1 bg-gray-700 rounded">
              <div 
                className="h-full bg-gradient-to-r from-yellow-400 to-green-400 rounded"
                style={{ width: `${65 + Math.random() * 25}%` }}
              ></div>
            </div>
            <span>{Math.round(65 + Math.random() * 25)}%</span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default QuickBetCard;